/**
 * ScanFlyerDialog — "📸 Scan flyer" feature.
 *
 * The killer feature borrowed (and improved) from Skylight: take a
 * photo of any school flyer / sports schedule / camp letter and
 * have the events appear in the family calendar.
 *
 * Flow:
 *   1. User taps "Scan flyer" on the calendar.
 *   2. We open the OS camera/file picker (this dialog hosts only the
 *      preview + confirm screen).
 *   3. Image gets shipped to `/api/events/from-image`, which calls
 *      Gemini (with the family's bring-your-own Google AI key) and
 *      returns a list of structured event candidates.
 *   4. We render each candidate as a checkbox card with confidence
 *      tag. Parent picks the ones they want.
 *   5. We bulk-create them via `/api/events` POST with the original
 *      photo attached so the family can scroll back to the source.
 *
 * Cost: zero to FamFeel — the family supplies the API key, Google's
 * own free tier (1500 calls/day on Flash) covers normal use. If they
 * haven't set up a key yet, we route them straight to settings.
 */
import React, { useEffect, useRef, useState } from "react";
import { X, Camera, CheckSquare, Square, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { kindOf, eventEmoji } from "@/lib/events";

// Squash an image down to ~1280px on the long side + JPEG-encode at
// 0.85 so we don't ship a 12 MP HEIC to Google. School flyers are
// usually printed A4; 1280px keeps every line of text legible while
// dropping the data-URL to ~300 KB on a typical photo.
async function squashImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const longEdge = 1280;
      const ratio = Math.min(1, longEdge / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Couldn't read that image"));
    };
    img.src = url;
  });
}

function fmtDateChip(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  } catch {
    return dateStr;
  }
}

function CandidateCard({ ev, picked, onToggle }) {
  const kind = kindOf(ev.title);
  const emoji = eventEmoji({ title: ev.title });
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`text-left w-full px-3 py-3 rounded-xl border transition-colors flex items-start gap-3 ${
        picked
          ? "border-[color:var(--accent)] bg-[color:var(--accent-soft,#FFE8A1)]/30"
          : "border-[color:var(--line)] bg-[color:var(--bg)]"
      }`}
      data-testid={`scan-candidate-${ev.id}`}
    >
      <span className="mt-0.5 flex-shrink-0">
        {picked ? (
          <CheckSquare className="w-5 h-5 text-[color:var(--accent)]" strokeWidth={2} />
        ) : (
          <Square className="w-5 h-5 text-[color:var(--ink-3)]" strokeWidth={2} />
        )}
      </span>
      <span
        className="w-9 h-9 rounded-full flex items-center justify-center text-xl flex-shrink-0"
        style={{ background: kind?.tint }}
        aria-hidden="true"
      >
        {emoji}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-display text-base leading-tight tracking-tight truncate">
          {ev.title}
        </span>
        <span className="block text-[12px] text-[color:var(--ink-2)] mt-0.5 truncate">
          {fmtDateChip(ev.date)}
          {ev.end_date && ev.end_date !== ev.date && ` → ${fmtDateChip(ev.end_date)}`}
          {ev.start_time && ` · ${ev.start_time}`}
          {ev.end_time && `–${ev.end_time}`}
          {ev.location && ` · ${ev.location}`}
        </span>
        {ev.confidence === "low" && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.06em] text-[color:var(--rough-strong)] mt-1">
            <AlertCircle className="w-3 h-3" strokeWidth={2} /> Low confidence — review
          </span>
        )}
      </span>
    </button>
  );
}

export default function ScanFlyerDialog({
  members,
  hasBYOKey,
  onOpenSettings,
  onCreated,
  onClose,
}) {
  const fileRef = useRef(null);
  const [stage, setStage] = useState("idle"); // idle | scanning | preview | inserting
  const [imageData, setImageData] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [picked, setPicked] = useState({});
  // Default attendees — every kid by default (school flyers are
  // overwhelmingly about the kids). Parents can re-assign in the
  // editor after insert if they want a different cast.
  const kidIds = (members || [])
    .filter((m) => m.role === "kid")
    .map((m) => m.id);
  const [attendees, setAttendees] = useState(kidIds);

  // Fire the file picker once on mount so the parent feels like
  // tapping "Scan flyer" *is* the camera.
  useEffect(() => {
    if (!hasBYOKey) return;
    if (stage === "idle") {
      // small delay so the browser tracks this as a user-gesture-driven
      // file picker on mobile Safari
      setTimeout(() => fileRef.current?.click(), 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFile = async (file) => {
    if (!file) return onClose();
    setStage("scanning");
    let dataUrl;
    try {
      dataUrl = await squashImage(file);
    } catch (e) {
      toast.error(e.message || "Couldn't read that image");
      setStage("idle");
      return;
    }
    setImageData(dataUrl);
    try {
      const lang = (navigator.language || "en").slice(0, 2);
      const out = await api.scanFlyerImage({ image: dataUrl, language_hint: lang });
      const evs = (out && out.events) || [];
      if (evs.length === 0) {
        toast.error("No events found on that flyer.");
        setStage("idle");
        onClose();
        return;
      }
      setCandidates(evs);
      const init = {};
      for (const e of evs) init[e.id] = e.confidence !== "low";
      setPicked(init);
      setStage("preview");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't scan that flyer";
      if (err?.response?.status === 412) {
        toast.error(msg, {
          action: { label: "Set up", onClick: () => onOpenSettings && onOpenSettings() },
        });
      } else {
        toast.error(msg);
      }
      setStage("idle");
      onClose();
    }
  };

  const onPickedChange = (id) =>
    setPicked((p) => ({ ...p, [id]: !p[id] }));

  const insertChosen = async () => {
    const chosen = candidates.filter((c) => picked[c.id]);
    if (chosen.length === 0) {
      toast.error("Pick at least one event to add");
      return;
    }
    setStage("inserting");
    let added = 0;
    for (const ev of chosen) {
      try {
        // Build ISO datetimes. If the model didn't extract a time, we
        // keep the event all-day — startsAt at midnight in the family's
        // local timezone. The backend's auto-categoriser will pick the
        // right `kind` from the title, so we don't bother sending one.
        const start = `${ev.date}T${ev.start_time || "00:00"}:00`;
        const endDate = ev.end_date || ev.date;
        const endTime = ev.end_time || (ev.start_time ? ev.start_time : "23:59");
        const end = `${endDate}T${endTime}:00`;
        await api.createEvent({
          title: ev.title,
          starts_at: start,
          ends_at: end,
          location: ev.location || undefined,
          notes: ev.notes || undefined,
          attendee_ids: attendees,
          attachment_image: imageData || undefined,
        });
        added += 1;
      } catch {/* ignore — keep inserting the rest */}
    }
    toast.success(`Added ${added} event${added === 1 ? "" : "s"} from your flyer`);
    onCreated && onCreated();
    onClose();
  };

  // Pre-key empty state
  if (!hasBYOKey) {
    return (
      <div
        className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="scan-flyer-noKey"
      >
        <div
          className="ff-card max-w-md w-full p-6 space-y-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="font-display-italic text-2xl tracking-tight">
            One-time setup needed
          </div>
          <p className="text-[14px] text-[color:var(--ink-2)] leading-snug">
            The flyer scanner uses your own free Google AI key (Google
            gives every Gmail account a generous free quota). Connect
            it once in Settings and you're set — the scan itself stays
            private to your family.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="ff-btn">
              Not now
            </button>
            <button
              onClick={() => {
                onOpenSettings && onOpenSettings();
                onClose();
              }}
              className="ff-btn ff-btn-primary"
              data-testid="scan-flyer-open-settings"
            >
              Set up
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="scan-flyer-dialog"
    >
      <div className="ff-card max-w-lg w-full p-5 md:p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="font-display-italic text-2xl tracking-tight">
            Scan flyer
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-[color:var(--bg)]/60"
            aria-label="Close"
            data-testid="scan-flyer-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0] || null)}
          data-testid="scan-flyer-file"
        />

        {stage === "scanning" && (
          <div className="py-12 flex flex-col items-center gap-3">
            {imageData && (
              <img
                src={imageData}
                alt="flyer preview"
                className="max-h-40 rounded-lg object-contain border border-[color:var(--line)]"
              />
            )}
            <div className="font-display-italic text-xl text-center">
              Reading your flyer…
            </div>
            <div className="label-tiny text-[color:var(--ink-3)]">
              This usually takes 4–6 seconds.
            </div>
          </div>
        )}

        {stage === "idle" && (
          <div className="py-10 flex flex-col items-center gap-3 text-center">
            <Camera className="w-10 h-10 text-[color:var(--ink-3)]" strokeWidth={1.5} />
            <p className="text-[14px] text-[color:var(--ink-2)]">
              Take a picture of any school flyer, sports schedule or
              camp letter — we'll lift the events into your calendar.
            </p>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="ff-btn ff-btn-primary"
              data-testid="scan-flyer-pick"
            >
              Choose photo
            </button>
          </div>
        )}

        {stage === "preview" && (
          <>
            {imageData && (
              <img
                src={imageData}
                alt="flyer preview"
                className="max-h-32 mx-auto rounded-lg object-contain border border-[color:var(--line)]"
              />
            )}
            <div className="text-[13px] text-[color:var(--ink-2)]">
              Found {candidates.length} event{candidates.length === 1 ? "" : "s"}.
              Tick the ones to add.
            </div>
            <div className="space-y-2">
              {candidates.map((ev) => (
                <CandidateCard
                  key={ev.id}
                  ev={ev}
                  picked={!!picked[ev.id]}
                  onToggle={() => onPickedChange(ev.id)}
                />
              ))}
            </div>

            {/* Attendee picker — defaults to all kids since school flyers
                overwhelmingly skew that way. Parents can flip toggles. */}
            {(members || []).length > 0 && (
              <div className="pt-2 border-t border-[color:var(--line-soft)]">
                <div className="label-tiny mb-2">Add to</div>
                <div className="flex flex-wrap gap-1.5">
                  {(members || [])
                    .filter((m) => m.role !== "pet")
                    .map((m) => {
                      const on = attendees.includes(m.id);
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() =>
                            setAttendees((cur) =>
                              cur.includes(m.id)
                                ? cur.filter((x) => x !== m.id)
                                : [...cur, m.id],
                            )
                          }
                          className={`ff-chip ${on ? "is-active" : ""}`}
                          data-testid={`scan-attendee-${m.id}`}
                        >
                          <span className="mr-1">{m.avatar_emoji}</span>
                          {m.name}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={onClose} className="ff-btn" data-testid="scan-cancel">
                Cancel
              </button>
              <button
                onClick={insertChosen}
                className="ff-btn ff-btn-primary"
                data-testid="scan-confirm"
              >
                Add {Object.values(picked).filter(Boolean).length} to calendar
              </button>
            </div>
          </>
        )}

        {stage === "inserting" && (
          <div className="py-12 flex items-center justify-center font-display-italic text-xl">
            Adding events…
          </div>
        )}
      </div>
    </div>
  );
}
